/**
 * Everything the app needs from the outside world, built once at start-up
 * from the runtime config and handed down (CLAUDE.md → explicit dependency
 * injection): the backend, the connectivity manager (which endpoint the
 * backend is — PLAN §10.2), the session store, the sign-in actions and the
 * theme. `start.tsx` builds the real ones; nothing below the views reaches for
 * them — presentational components get props.
 */
import { type ApiClient, createApiClient } from '@acp/api-client/client.js';
import { PROBE_TIMEOUT_MS, probeHealth } from '@acp/api-client/health.js';
import type { FetchPort } from '@acp/api-client/ports.js';
import type { ContractParsing } from '@acp/api-contract/parsing.js';
import { type Backend, createBackend, sessionStorageKey } from '../api/backend.js';
import { type Endpoint, statusEndpoints, watchedEndpoints } from '../connectivity/endpoints.js';
import { createFailoverApi, RESEND_WAIT_MS } from '../connectivity/failover.js';
import { type ConnectivityManager, createConnectivityManager } from '../connectivity/manager.js';
import { type CognitoConfig, cognitoLogin, type LoginOutcome, refreshCognitoSession, type SrpLib } from '../auth/cognito.js';
import { devLogin, refreshDevSession } from '../auth/devLogin.js';
import { exchangeForLocalSession, localLogin, refreshLocalSession } from '../auth/localSession.js';
import type { LocalAuthStatus } from '@acp/api-contract/offlineAuth.js';
import { readLocalAuth } from '../api/localAuth.js';
import { createSessionStore, type Session, type SessionStore, type StoragePort } from '../auth/session.js';
import type { RuntimeConfig } from '@acp/api-contract/runtimeConfig.js';
import { createChangeLog, type ChangeLog } from '../lib/live/changeLog.js';
import { createDismissalStore, type DismissalStore } from '../lib/dismissals.js';
import { resolveEndpoints } from '../lib/runtimeConfig.js';
import { applyTheme, initialTheme, THEME_STORAGE_KEY, type Theme } from '../lib/theme.js';

/** How this backend signs people in, as the login view needs it. */
export type AuthActions =
  | { readonly kind: 'dev'; readonly login: (userId: string) => Promise<void> }
  | { readonly kind: 'cognito'; readonly login: (email: string, password: string) => Promise<LoginOutcome> };

/**
 * A node's own sign-in (D20), present when the SPA is served by a node: what `GET /local/auth` says right now,
 * and break-glass sign-in (`POST /local/login`) — an offline PIN or the site's emergency account, accepted only
 * while the node cannot reach the cloud.
 */
export type OfflineActions = {
  readonly status: () => Promise<LocalAuthStatus>;
  readonly login: (account: string, secret: string) => Promise<void>;
};

export type ThemeStore = {
  readonly get: () => Theme;
  readonly set: (theme: Theme) => void;
  readonly subscribe: (listener: () => void) => () => void;
};

export type Services = {
  readonly config: RuntimeConfig;
  readonly backend: Backend;
  /** The page's own backend with no token: the routes that take none (an invite link's set-password page, 7b). */
  readonly publicApi: ApiClient;
  readonly connectivity: ConnectivityManager;
  readonly sessions: SessionStore;
  readonly auth: AuthActions;
  /** Null unless the page is served by a node. */
  readonly offline: OfflineActions | null;
  readonly theme: ThemeStore;
  /** The wall clock (for "last answered 12 s ago"). */
  readonly nowMs: () => number;
  /** How answers and WS frames are read: tolerant in the app, strict in tests (D35, `@acp/api-contract/parsing.js`). */
  readonly responses: ContractParsing;
  /** The last change the tenant change channel announced per resource (D35): what an open editor learns who changed it from. */
  readonly liveChanges: ChangeLog;
  /** The announcements this browser dismissed (phase 7b's banner; a per-viewer convenience in storage). */
  readonly dismissals: DismissalStore;
};

export type ServiceDeps = {
  readonly config: RuntimeConfig;
  /** The page's origin, against which relative endpoints resolve. */
  readonly origin: string;
  readonly fetch: FetchPort;
  readonly storage: StoragePort;
  readonly nowMs: () => number;
  /** Run `fn` after `ms`; returns a cancel function (the connectivity probes' schedule). */
  readonly after: (ms: number, fn: () => void) => () => void;
  /** The SRP library for a Cognito pool (injected: `realSrpLib` in the app, a fake in tests). */
  readonly srpLib: (config: CognitoConfig) => SrpLib;
  /** The document root the theme class goes on. */
  readonly root: { readonly classList: { toggle: (name: string, force: boolean) => unknown } };
  /**
   * How answers and WS frames are read (D35): `'tolerant'` in the app — a page built (or cached by the service worker)
   * before an additive API change keeps working against the newer server — `'strict'` in tests.
   */
  readonly responses: ContractParsing;
};

export function createThemeStore(storage: StoragePort, root: ServiceDeps['root']): ThemeStore {
  let theme = initialTheme(storage.get(THEME_STORAGE_KEY));
  applyTheme(root, theme);
  const listeners = new Set<() => void>();
  return {
    get: () => theme,
    set: (next) => {
      theme = next;
      storage.set(THEME_STORAGE_KEY, next);
      applyTheme(root, next);
      for (const listener of listeners) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export function createServices(deps: ServiceDeps): Services {
  const { config } = deps;
  const auth = config.auth;
  const { apiUrl } = resolveEndpoints(config.backend, deps.origin);
  // The node the SPA talks to now (after a failover, the peer — which accepts the same local session, D20).
  let nodeApiUrl = (): string => apiUrl;
  const refreshLocal = refreshLocalSession(deps.fetch, () => nodeApiUrl(), deps.nowMs);
  const refreshUpstream = auth.kind === 'cognito' ? (s: Session) => refreshCognitoSession(deps.fetch, auth, s) : refreshDevSession;
  const refresh = (s: Session): Promise<Session | null> => (s.provider === 'local' ? refreshLocal(s) : refreshUpstream(s));
  const servedByNode = config.backend.kind === 'node';
  // On a node's page, the Cognito/dev session gets a local session held beside it (D20): node calls send that,
  // cloud calls (the connectivity panel's GET /status) keep the Cognito token.
  const exchangeLocal = servedByNode
    ? async (token: string) => {
        const local = await exchangeForLocalSession(deps.fetch, nodeApiUrl(), token);
        return local === null ? null : { idToken: local.idToken, expiresAtMs: local.expiresAtMs };
      }
    : null;
  const sessions = createSessionStore({ storage: deps.storage, storageKey: sessionStorageKey(config, apiUrl), refresh, refreshLocal, exchangeLocal, nowMs: deps.nowMs });
  const clients = new Map<string, ApiClient>();
  const clientFor = (endpoint: Endpoint): ApiClient => {
    const existing = clients.get(endpoint.id);
    if (existing !== undefined) return existing;
    // A node is sent the local session (when one is held), the cloud the Cognito token (`chooseToken`).
    const client = createApiClient({ baseUrl: endpoint.apiUrl, fetch: deps.fetch, token: endpoint.kind === 'node' ? sessions.nodeToken : sessions.idToken, responses: deps.responses });
    clients.set(endpoint.id, client);
    return client;
  };
  const statusClient = (cloud: Endpoint): ApiClient => createApiClient({ baseUrl: cloud.apiUrl, fetch: deps.fetch, token: () => sessions.idToken(false), responses: deps.responses });
  const connectivity = createConnectivityManager({
    endpoints: watchedEndpoints(config, deps.origin),
    pageKind: config.backend.kind,
    probe: (endpoint) => probeHealth({ fetch: deps.fetch, nowMs: deps.nowMs, after: deps.after }, endpoint.apiUrl, PROBE_TIMEOUT_MS),
    // The cloud's view needs a token the cloud accepts — the same Cognito session (§11). Its token is refreshed near
    // expiry like any request's, but a 401 never FORCES a refresh: the cloud refusing a token it cannot verify (a
    // node's dev token) must not sign the operator out.
    readCloudView: async (cloud) => {
      if (sessions.getSession() === null) return null;
      return statusClient(cloud).call('getStatus', { params: null, query: null, body: null });
    },
    // A cloud page learns the tenant's nodes from the cloud's view (its `/config.json` is every tenant's).
    learnEndpoints: (view, known) => statusEndpoints(view, config, deps.origin, known),
    after: deps.after,
    nowMs: deps.nowMs,
  });
  const backend = createBackend(connectivity.primary, createFailoverApi({ manager: connectivity, clientFor, resendWaitMs: RESEND_WAIT_MS }));
  nodeApiUrl = () => backend.apiUrl;
  /** Sign in; on a node's page, also get the local session to hold beside it (D20) — without it, the own token serves. */
  const signInHere = async (session: Session): Promise<void> => {
    sessions.signIn(session);
    if (servedByNode) await sessions.nodeToken(false).catch(() => undefined);
  };
  const actions: AuthActions =
    auth.kind === 'cognito'
      ? {
          kind: 'cognito',
          login: async (email, password) => {
            const outcome = await cognitoLogin(deps.srpLib(auth), email, password);
            return signingIn(outcome, signInHere);
          },
        }
      : {
          kind: 'dev',
          login: async (userId) => {
            await signInHere(await devLogin(deps.fetch, apiUrl, userId));
          },
        };
  const offline: OfflineActions | null = servedByNode
    ? {
        status: async () => readLocalAuth(deps.fetch, nodeApiUrl()),
        login: async (account, secret) => {
          sessions.signIn(await localLogin(deps.fetch, nodeApiUrl(), account, secret));
        },
      }
    : null;
  const publicApi = createApiClient({ baseUrl: apiUrl, fetch: deps.fetch, token: null, responses: deps.responses });
  return { config, backend, publicApi, connectivity, sessions, auth: actions, offline, theme: createThemeStore(deps.storage, deps.root), nowMs: deps.nowMs, responses: deps.responses, liveChanges: createChangeLog(), dismissals: createDismissalStore(deps.storage) };
}

/** Store the session when an outcome (or a completed new-password challenge) signs in. */
async function signingIn(outcome: LoginOutcome, signIn: (session: Session) => Promise<void>): Promise<LoginOutcome> {
  if (outcome.type === 'signed-in') {
    await signIn(outcome.session);
    return outcome;
  }
  return { type: 'new-password-required', complete: async (newPassword) => signingIn(await outcome.complete(newPassword), signIn) };
}
